import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import ApplicationModal from "src/components/DealerGeneratedApplication/ApplicationModal";
import { ApplicationLockTakeoverDialog } from "src/components/DealerGeneratedApplication";
import { useDealer } from "src/hooks/useDealer";
import {
  useOpenDgaModalWithLock,
  type OpenDgaModalWithLockApp,
  type OpenDgaModalWithLockOptions,
} from "src/hooks/useOpenDgaModalWithLock";
import { createDgaLockChannel } from "src/utils/dgaLockChannel";

interface DgaModalContextType {
  openDgaModal: (applicationId?: string) => void;
  closeDgaModal: () => Promise<void>;
  registerCloseHandler: (handler: (() => Promise<void>) | null) => void;
  openDgaModalWithLock: (
    app: OpenDgaModalWithLockApp,
    options?: OpenDgaModalWithLockOptions,
  ) => void;
  checkingLockAppId: string | null;
  subscribeToApplicationListRefresh: (onRefresh: () => void) => () => void;
}

interface DgaModalProviderProps {
  children: ReactNode;
}

const DgaModalContext = createContext<DgaModalContextType | null>(null);

export const DgaModalProvider = ({ children }: DgaModalProviderProps) => {
  const { dealer_id } = useDealer();
  const [isOpen, setIsOpen] = useState(false);
  const [applicationId, setApplicationId] = useState<string | undefined>();
  const [modalKey, setModalKey] = useState(0);
  const applicationIdRef = useRef<string | undefined>(undefined);
  const lockChannelRef = useRef<ReturnType<typeof createDgaLockChannel> | null>(
    null,
  );
  const refreshSubscribersRef = useRef(new Set<() => void>());
  const closeHandlerRef = useRef<(() => Promise<void>) | null>(null);

  const openDgaModal = useCallback((appId?: string) => {
    setApplicationId(appId);
    setModalKey((k) => k + 1);
    setIsOpen(true);
  }, []);

  const registerCloseHandler = useCallback(
    (handler: (() => Promise<void>) | null) => {
      closeHandlerRef.current = handler;
    },
    [],
  );

  const closeDgaModal = useCallback(async () => {
    const handler = closeHandlerRef.current;
    if (handler) {
      await handler();
      return;
    }
    setIsOpen(false);
    setApplicationId(undefined);
  }, []);

  const {
    checkingLockAppId,
    openDgaModalWithLock,
    showTakeoverDialog,
    closeTakeoverDialog,
    takeoverLockedByUser,
    isTakingOver,
    handleTakeover,
  } = useOpenDgaModalWithLock(dealer_id, lockChannelRef, openDgaModal);

  useEffect(() => {
    applicationIdRef.current = applicationId;
  }, [applicationId]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setApplicationId(undefined);
    }
  }, []);

  const notifyApplicationListRefresh = useCallback(() => {
    refreshSubscribersRef.current.forEach((onRefresh) => onRefresh());
  }, []);

  const subscribeToApplicationListRefresh = useCallback(
    (onRefresh: () => void) => {
      refreshSubscribersRef.current.add(onRefresh);
      return () => {
        refreshSubscribersRef.current.delete(onRefresh);
      };
    },
    [],
  );

  useEffect(() => {
    if (!dealer_id) return;
    const channel = createDgaLockChannel(dealer_id);
    lockChannelRef.current = channel;

    const unsubscribeTakeover = channel.subscribe((appId) => {
      if (applicationIdRef.current === appId) {
        void closeDgaModal();
      }
    });
    const unsubscribeRefresh = channel.subscribeRefresh(
      notifyApplicationListRefresh,
    );

    return () => {
      unsubscribeTakeover();
      unsubscribeRefresh();
      lockChannelRef.current = null;
      channel.close();
    };
  }, [dealer_id, closeDgaModal, notifyApplicationListRefresh]);

  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      notifyApplicationListRefresh();
      lockChannelRef.current?.postRefresh();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, notifyApplicationListRefresh]);

  return (
    <DgaModalContext.Provider
      value={{
        openDgaModal,
        closeDgaModal,
        registerCloseHandler,
        openDgaModalWithLock,
        checkingLockAppId,
        subscribeToApplicationListRefresh,
      }}
    >
      {children}
      <ApplicationModal
        key={modalKey}
        open={isOpen}
        onOpenChange={handleOpenChange}
        applicationId={applicationId}
      />
      <ApplicationLockTakeoverDialog
        open={showTakeoverDialog}
        onOpenChange={(open) => {
          if (!open) {
            closeTakeoverDialog();
          }
        }}
        onTakeover={handleTakeover}
        lockedByUser={takeoverLockedByUser}
        isTakingOver={isTakingOver}
      />
    </DgaModalContext.Provider>
  );
};

export const useDgaModal = () => {
  const context = useContext(DgaModalContext);
  if (!context) {
    throw new Error("useDgaModal must be used within a DgaModalProvider");
  }
  return context;
};
